// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AnalyticsCommands from '@app/features/analytics/commands/AnalyticsCommands';
import type {Channel} from '@app/features/channel/models/Channel';
import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import {Logger} from '@app/features/platform/utils/AppLogger';
import TableGames from '@app/features/table_game/state/TableGames';
import {drawTableTop} from '@app/features/table_game/utils/drawTableTop';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {VoiceSceneActionDock} from '@app/features/voice_scene/components/VoiceSceneActionDock';
import {VoiceSceneOverlay} from '@app/features/voice_scene/components/VoiceSceneOverlay';
import {VoiceSceneRoomThemeSelect} from '@app/features/voice_scene/components/VoiceSceneRoomThemeSelect';
import {
	VoiceSceneShortcutCard,
	VoiceSceneShortcutIntro,
} from '@app/features/voice_scene/components/VoiceSceneShortcutCard';
import {VoiceSceneToolbar} from '@app/features/voice_scene/components/VoiceSceneToolbar';
import styles from '@app/features/voice_scene/components/VoiceSceneView.module.css';
import type {VoiceSceneRenderer} from '@app/features/voice_scene/scene/VoiceSceneRenderer';
import type {VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomShortcutGuide from '@app/features/voice_scene/state/RoomShortcutGuide';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {effectiveRoomTheme} from '@app/features/voice_scene/utils/EffectiveRoomTheme';
import {roomFileUrl} from '@app/features/voice_scene/utils/RoomFileUrl';
import {themeTableSlots, type VoiceRoomTheme} from '@fluxer/constants/src/VoiceRoomThemes';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {reaction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {type ComponentType, type CSSProperties, useEffect, useLayoutEffect, useRef, useState} from 'react';

const logger = new Logger('VoiceSceneView');

const CONTEXT_LOST_DESCRIPTOR = msg({
	message: '3D view stopped. Showing the flat view.',
	comment:
		'Voice call 3D room: toast when the graphics card dropped the 3D view (driver reset, sleep). The call keeps going in the flat view.',
});

type Rgb = readonly [number, number, number];

// The floor end of the backdrop gradient, as a share of the room colour (linear).
const BACKDROP_FLOOR = 0.3;

const srgbByte = (linear: number): number => {
	const c = Math.min(1, Math.max(0, linear));
	return Math.round(255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055));
};
const cssRgb = (rgb: Rgb, scale: number): string => `rgb(${rgb.map((c) => srgbByte(c * scale)).join(', ')})`;

// The room's backdrop colour (linear RGB from its file) as the stage's gradient behind the transparent canvas; no
// colour, or a broken one, keeps the plain black stage.
export function backdropStyle(backdrop: Rgb | null): CSSProperties | undefined {
	if (!backdrop?.every(Number.isFinite)) return undefined;
	return {
		'--voice-scene-backdrop-top': cssRgb(backdrop, 1),
		'--voice-scene-backdrop-bottom': cssRgb(backdrop, BACKDROP_FLOOR),
	} as CSSProperties;
}

// Public table faces on the 3D tables; a state change redraws that one texture and asks for a single frame. An ended
// table has nobody at it: its view stays in the store, its board leaves the table.
function useTableTops(channelId: string, theme: VoiceRoomTheme | null, handle: VoiceSceneHandle | null): void {
	useEffect(() => {
		if (!theme || !handle) return;
		const disposers = themeTableSlots(theme).map((slot) => {
			const canvas = document.createElement('canvas');
			return reaction(
				() => {
					const view = TableGames.getView(channelId, slot);
					return view?.summary.status === 'ended' ? null : view;
				},
				(view) => {
					if (!view) {
						handle.setTableTop(slot, null);
						return;
					}
					drawTableTop(canvas, view);
					handle.setTableTop(slot, canvas);
				},
				{fireImmediately: true},
			);
		});
		return () => {
			for (const dispose of disposers) dispose();
		};
	}, [channelId, theme, handle]);
}

// Preview builds only: the candidate character picker, in its own chunk. Without PUBLIC_ROOM_CHARACTER_PREVIEW=1 the
// branch is dead at build time and the chunk is never made.
function useCharacterPreview(): ComponentType | null {
	const [picker, setPicker] = useState<ComponentType | null>(null);
	useEffect(() => {
		let live = true;
		if (import.meta.env.PUBLIC_ROOM_CHARACTER_PREVIEW === '1')
			import('@app/features/voice_scene/components/VoiceSceneCharacterPreview')
				.then((module) => {
					if (live) setPicker(() => module.VoiceSceneCharacterPreview);
				})
				.catch((error: unknown) => logger.warn('Character preview picker failed to load', error));
		return () => {
			live = false;
		};
	}, []);
	return picker;
}

export const VoiceSceneView = observer(function VoiceSceneView({channel}: {channel: Channel}) {
	const {i18n} = useLingui();
	const stageRef = useRef<HTMLDivElement>(null);
	const [handle, setHandle] = useState<VoiceSceneHandle | null>(null);
	const [standing, setStanding] = useState(false);
	const theme = effectiveRoomTheme(
		channel.roomTheme,
		readVoiceRoomAssignment(ExperimentAssignments.response).default_room_theme,
		channel.type,
	);
	const contextLostText = i18n._(CONTEXT_LOST_DESCRIPTOR);
	useEffect(() => (theme ? AnalyticsCommands.openScreen('voice_scene') : undefined), [theme]);

	// Layout cleanup runs before React takes the stage out of the document: OrbitControls removes its keydown
	// listener from the canvas's root node at dispose, and a detached canvas would leave it on the document.
	useLayoutEffect(() => {
		const container = stageRef.current;
		if (!theme || !container) return;
		const abort = new AbortController();
		let renderer: VoiceSceneRenderer | null = null;
		// The only path to three: a lazy chunk fetched when a room opens (a join may have started it already).
		import('@app/features/voice_scene/scene/VoiceSceneRenderer')
			.then(({createVoiceSceneRenderer}) => {
				performance.mark('oda-3b:chunk');
				return createVoiceSceneRenderer({
					container,
					url: roomFileUrl(theme),
					signal: abort.signal,
					onContextLost: () => {
						ToastCommands.createToast({type: 'info', children: contextLostText});
						VoiceSceneSettings.reportFailure('context_lost');
					},
				});
			})
			.then((created) => {
				if (abort.signal.aborted) {
					created.dispose();
					return;
				}
				renderer = created;
				setHandle(created.handle);
			})
			.catch((error: unknown) => {
				if (abort.signal.aborted) return;
				logger.warn('3D room failed to load, showing the flat view', error);
				VoiceSceneSettings.reportFailure('load_failed');
			});
		return () => {
			abort.abort();
			renderer?.dispose();
			setHandle(null);
		};
	}, [theme]);

	// The open chat lies over the room's right side: the canvas keeps its size, so the zoom stays, and the room slides
	// to the middle of what is left.
	const chatOpen = VoiceSceneSettings.chatOpen;
	useLayoutEffect(() => {
		if (!handle) return;
		const stage = stageRef.current;
		const toolbar = stage?.parentElement?.querySelector('[data-flx="voice-scene.voice-scene-toolbar.toolbar"]');
		const covered =
			chatOpen && stage && toolbar ? stage.getBoundingClientRect().right - toolbar.getBoundingClientRect().left : 0;
		handle.coverRight?.(Math.max(0, covered));
	}, [handle, chatOpen]);

	useTableTops(channel.id, theme, handle);
	const CharacterPreview = useCharacterPreview();
	const backdrop = backdropStyle(handle?.layout.look?.backdrop ?? null);
	const canvas = handle ? (stageRef.current?.querySelector('canvas') ?? null) : null;

	return (
		<div
			className={styles.scene}
			style={backdrop}
			data-backdrop={backdrop ? '' : undefined}
			data-chat-open={chatOpen ? '' : undefined}
			data-dock-hidden={RoomShortcutGuide.dockHidden ? '' : undefined}
			data-flx="voice-scene.voice-scene-view.scene"
		>
			<div
				ref={stageRef}
				className={clsx(styles.stage, handle && styles.stageReady)}
				data-flx="voice-scene.voice-scene-view.stage"
			/>
			{handle ? (
				<VoiceSceneOverlay
					handle={handle}
					channel={channel}
					onStandingChange={setStanding}
					data-flx="voice-scene.voice-scene-view.voice-scene-overlay"
				/>
			) : (
				<div className={styles.loading} data-flx="voice-scene.voice-scene-view.loading">
					<Spinner data-flx="voice-scene.voice-scene-view.spinner" />
				</div>
			)}
			<VoiceSceneToolbar
				handle={handle}
				channel={channel}
				standing={standing}
				canvas={canvas}
				data-flx="voice-scene.voice-scene-view.voice-scene-toolbar"
			/>
			<VoiceSceneActionDock
				handle={handle}
				standing={standing}
				canvas={canvas}
				data-flx="voice-scene.voice-scene-view.voice-scene-action-dock"
			/>
			{handle ? (
				<>
					<VoiceSceneShortcutIntro data-flx="voice-scene.voice-scene-view.voice-scene-shortcut-intro" />
					<VoiceSceneShortcutCard canvas={canvas} data-flx="voice-scene.voice-scene-view.voice-scene-shortcut-card" />
				</>
			) : null}
			<VoiceSceneRoomThemeSelect channel={channel} data-flx="voice-scene.voice-scene-view.room-theme-select" />
			{CharacterPreview ? <CharacterPreview /> : null}
		</div>
	);
});
